import { loginUrl } from './util';
import request from '@/utils/request';
import type {
    ICurrentPlanInfo,
    IShareInfo,
    IUserPointsInfo,
    ICouponDetail,
    ShareType,
    IProductItem,
    IBlogItem,
    ILeaderboard,
    ICurrentPlanTop,
    IWalletItem,
    IFaqInfo,
} from '@/components/Referral/index.d';

export type ResponseObj<T = unknown> = {
    code: string;
    data: T;
    message: string;
};

/**
 * 账户积分详情
 */
export const getReferralPoints = (locale: string): Promise<ResponseObj<IUserPointsInfo>> =>
    request.get(`${loginUrl(locale)}/integral/web/referralPlan/getReferralPoints`, {
        needToken: true,
    });

/**
 * 获取当前裂变计划 newInterface d3ms专用接口
 */
export const getCurrentPlan = (locale: string, path?: string, newInterface = false): Promise<ResponseObj<ICurrentPlanInfo>> =>
    newInterface
        ? request.post(`${loginUrl(locale)}/integral/web/referralPlan/getCurrentPlanCampaign`, {
              data: {
                  ...(path ? { url: path } : {}),
              },
              needToken: true,
          })
        : request.post(`${loginUrl(locale)}/integral/web/referralPlan/getCurrentPlan`, {
              data: {
                  ...(path ? { url: path } : {}),
              },
              needToken: true,
          });

/**
 * 通过inviteCode获取当前裂变计划
 */
export const getCurrentPlanByInviteCode = (locale: string, inviteCode: string): Promise<ResponseObj<ICurrentPlanInfo>> =>
    request.post(`${loginUrl(locale)}/integral/web/referralPlan/getPlanByInviteCode`, {
        data: {
            inviteCode,
        },
        needToken: true,
    });

/**
 * 获取当前裂变排名
 * lastMon  0-本月排行;1-上月排行
 */
export const getCurrentPlanTop = (
    data: { page?: number; size: number; planId: string; lastMon: number },
    locale: string,
): Promise<ResponseObj<{ content: ICurrentPlanTop[] }>> => request.post(`${loginUrl(locale)}/integral/web/referralPlan/pageTopN`, { data, needToken: true });

/**
 * 获取用户分享信息
 */
export const getShareInfo = (data: { planId: string; shareUrl: string }, terminalId: string, locale: string): Promise<ResponseObj<IShareInfo>> =>
    request.post(`${loginUrl(locale)}/integral/web/referralPlan/getShareInfo`, {
        data,
        headers: {
            terminalId,
        },
        needToken: true,
    });

/**
 * 发送邮件邀请
 */
export const sendInviteEmail = (
    data: {
        planId: string;
        shareEmail: string;
        shareId: string;
        shareUrl: string;
    },
    locale: string,
): Promise<ResponseObj<IShareInfo> | null> => {
    const { shareUrl } = data;
    if (shareUrl.startsWith(window.location.origin)) {
        return request.post(`${loginUrl(locale)}/integral/web/referralPlan/sendInviteEmail`, { data, needToken: true });
    }
    return Promise.resolve(null);
};

/**
 * 获取裂变所有奖励
 */
export const getPageRewards = (
    data: {
        awardType?: string;
        page?: number;
        planId?: string;
        shareType: ShareType;
        size: number;
    },
    locale: string,
): Promise<ResponseObj<{ content: ICouponDetail[] }>> => request.post(`${loginUrl(locale)}/integral/web/referralPlan/pageRewards`, { data, needToken: true });

/**
 * 查询个人排行榜
 */
export const getLeaderboard = (data: { page?: number; planId?: string; size: number }, locale: string): Promise<ResponseObj<{ content: ILeaderboard[] }>> =>
    request.post(`${loginUrl(locale)}/integral/web/referralPlan/pageLeaderboard`, {
        data,
        needToken: true,
    });

/**
 * 查询产品列表
 */
export const getProductList = (data: { bizModule: string; module: string }, locale: string): Promise<ResponseObj<IProductItem[]>> =>
    request.post(`${loginUrl(locale)}/integral/web/rewardProduct/listProducts`, { data, needToken: true });

/**
 * 查询blog列表
 */
export const getBlogList = (locale: string): Promise<ResponseObj<IBlogItem[]>> =>
    request.post(`${loginUrl(locale)}/integral/web/referralPlan/listBlogs`, { data: {}, needToken: true });

/**
 * 校验邀请码
 */
export const checkInviteCode = (
    data: {
        inviteCode: string;
    },
    locale: string,
): Promise<ResponseObj<any>> =>
    request.post(`${loginUrl(locale)}/integral/web/referralPlan/checkInviteCode`, {
        data: {
            inviteCode: data.inviteCode,
        },
        // headers: {
        //     terminalId: data.deviceId,
        // },
        needToken: true,
    });

/**
 * 获取裂变积分奖励
 */
export const getPoints = (
    data: { page?: number; size: number; shareType: ShareType; planId?: string },
    locale: string,
): Promise<ResponseObj<{ content: ICouponDetail[] }>> => request.post(`${loginUrl(locale)}/integral/web/referralPlan/pagePoints`, { data, needToken: true });

/**
 * 获取裂变优惠券奖励
 */
export const getDiscount = (
    data: { page?: number; size: number; shareType: ShareType; planId?: string },
    locale: string,
): Promise<ResponseObj<{ content: ICouponDetail[] }>> => request.post(`${loginUrl(locale)}/integral/web/referralPlan/pageDiscount`, { data, needToken: true });

/**
 * 获取裂变礼品卡
 */
export const getGiftCard = (
    data: { page?: number; size: number; shareType: ShareType; planId?: string },
    locale: string,
): Promise<ResponseObj<{ content: ICouponDetail[] }>> => request.post(`${loginUrl(locale)}/integral/web/referralPlan/pageGiftCard`, { data, needToken: true });

// 个人中心

/**
 * 获取裂变积分奖励
 */
export const getUserPoints = (
    data: { page?: number; size: number; shareType: ShareType },
    locale: string,
): Promise<ResponseObj<{ content: ICouponDetail[] }>> =>
    request.post(`${loginUrl(locale)}/integral/web/referralPlan/myAccount/pagePoints`, { data, needToken: true });

/**
 * 获取裂变优惠券
 */
export const getUserDiscount = (
    data: { page?: number; size: number; shareType: ShareType; planId?: string },
    locale: string,
): Promise<ResponseObj<{ content: ICouponDetail[] }>> =>
    request.post(`${loginUrl(locale)}/integral/web/referralPlan/myAccount/pageDiscount`, { data, needToken: true });

/**
 * 获取裂变礼品卡奖励(包括第三方优惠券)
 */
export const getUserGiftCard = (
    data: { page?: number; size: number; shareType: ShareType; planId?: string },
    locale: string,
): Promise<ResponseObj<{ content: ICouponDetail[] }>> =>
    request.post(`${loginUrl(locale)}/integral/web/referralPlan/myAccount/pageGiftCard`, { data, needToken: true });

/**
 * 账户积分详情
 */
export const getReferralCount = (locale: string, planId: string): Promise<ResponseObj<IUserPointsInfo>> =>
    request.post(`${loginUrl(locale)}/integral/web/referralPlan/getReferralCount`, {
        needToken: true,
        data: {
            planId,
        },
    });

/**
 * 查询站点最小提现金额配置
 */
export const getWalletMinAmount = (
    locale: string,
): Promise<
    ResponseObj<{
        withdrawalCurrency: string;
        withdrawalCurrencySymbol: string;
        withdrawalMinAmount: number;
    }>
> =>
    request.get(`${loginUrl(locale)}/order/payment/queryWithdrawalMinAmount`, {
        needToken: true,
    });

/**
 * 查询用户钱包详情
 */
export const getWalletList = (locale: string): Promise<ResponseObj<IWalletItem[]>> =>
    request.get(`${loginUrl(locale)}/order/payment/queryWalletList?sourceService=integral`, {
        needToken: true,
    });

/**
 * 获取裂变所有cash
 */
export const getPageCash = (
    data: {
        awardType?: string;
        page?: number;
        planId?: string;
        shareType: ShareType;
        size: number;
    },
    locale: string,
): Promise<ResponseObj<{ content: ICouponDetail[] }>> =>
    request.post(`${loginUrl(locale)}/integral/web/referralPlan/myAccount/pageCash`, { data, needToken: true });

/**
 * 获取referral的faq
 */
export const getReferralFaq = (locale: string, planId: string): Promise<ResponseObj<IFaqInfo>> =>
    request.post(`${loginUrl(locale)}/integral/web/referralPlan/getFaq`, {
        data: {
            planId,
        },
    });
